/**
 * Client-side trackEvent helper for firing one-off custom events
 * (share_whatsapp, share_calendar, copy_link, rsvp_click, etc.) to
 * /api/analytics/ingest.
 *
 * Re-uses the same session_id and UTM params that the Tracker component
 * stores in sessionStorage so events tie back to the same visitor session.
 *
 * Uses navigator.sendBeacon when available (survives page navigation like
 * window.open for WhatsApp share) and falls back to fetch with keepalive.
 */

const INGEST_ENDPOINT = "/api/analytics/ingest";

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem("_iv_sid");
}

function getUTM(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
} {
  if (typeof window === "undefined") return {};
  return {
    utm_source: sessionStorage.getItem("_iv_utm_source") || undefined,
    utm_medium: sessionStorage.getItem("_iv_utm_medium") || undefined,
    utm_campaign: sessionStorage.getItem("_iv_utm_campaign") || undefined,
  };
}

export interface TrackEventInput {
  event_type: string;
  payload?: Record<string, unknown>;
}

export function trackEvent({ event_type, payload }: TrackEventInput): void {
  if (typeof window === "undefined") return;
  const session_id = getSessionId();
  if (!session_id) return; // Tracker hasn't initialized — drop silently

  const evt = {
    event_type,
    session_id,
    page_path: window.location.pathname,
    payload: payload ?? {},
    referrer: document.referrer || undefined,
    ...getUTM(),
  };

  const body = JSON.stringify({ events: [evt] });

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(INGEST_ENDPOINT, blob);
      if (ok) return;
    }
  } catch {
    // fall through to fetch
  }

  fetch(INGEST_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    /* analytics must never break the site */
  });
}
